import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/apiClient";
import { getAuthUser, saveAuthUser } from "../utils/auth";
import { getReferralCode, clearReferralCode } from "../utils/referralSession";

const Signup = () => {
  const navigate = useNavigate();
  const referralCode = useMemo(() => getReferralCode(), []);
  
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    password: "",
    isAbove18: false,
  });
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const googleButtonRef = useRef(null);

  // Check if user is already logged in
  useEffect(() => {
    const existing = getAuthUser();
    if (existing) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) {
      setStatus("error");
      setMessage("Google authentication failed. Please try again.");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const authResponse = await apiFetch("/auth/google", {
        method: "POST",
        data: {
          credential: response.credential,
          isSignup: true,
          referralCode: referralCode,
        },
      });

      setStatus("success");
      setMessage(authResponse?.message || "Signup successful. Redirecting…");

      if (authResponse?.user) {
        saveAuthUser(authResponse.user);
      }
      clearReferralCode();

      setTimeout(() => {
        navigate("/packages", { replace: true });
      }, 1200);
    } catch (error) {
      setStatus("error");
      let errorMsg = "Google signup failed.";
      
      if (error.message === "Network request failed" || error.cause) {
        errorMsg = "Unable to connect to server. Please check your internet connection and ensure the backend is running.";
      } else if (error?.data?.message) {
        errorMsg = error.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setMessage(errorMsg);
      console.error("Google signup error:", error);
    } finally {
      setStatus((current) => (current === "success" ? "success" : "idle"));
    }
  }, [navigate, referralCode]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn('Google Client ID not configured');
      return;
    }

    const initializeGoogleButton = () => {
      if (!googleButtonRef.current) return;

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleResponse,
        });

        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          width: '100%',
          text: 'signup_with',
        });
      } catch (error) {
        console.error('Failed to initialize Google button:', error);
      }
    };

    if (window.google && window.google.accounts && window.google.accounts.id) {
      initializeGoogleButton();
    } else {
      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if (window.google && window.google.accounts && window.google.accounts.id) {
            initializeGoogleButton();
          }
        });
        return;
      }

      // Load Google script
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
          initializeGoogleButton();
        }
      };
      script.onerror = () => {
        console.error('Failed to load Google Identity Services script');
      };
      document.head.appendChild(script);
    }
  }, [handleGoogleResponse, referralCode]);

  // Handle form input changes
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("loading");
    setMessage("");

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      city: formData.city.trim(),
      password: formData.password,
      isAbove18: formData.isAbove18,
    };

    if (referralCode) {
      const normalizedReferralCode = referralCode.trim();
      if (normalizedReferralCode) {
        payload.referralCode = normalizedReferralCode;
      }
    }

    // Validation
    if (!payload.name || !payload.email || !payload.phone || !payload.city || !payload.password) {
      setStatus("error");
      setMessage("Please fill in all required fields.");
      return;
    }

    if (payload.password.length < 6) {
      setStatus("error");
      setMessage("Password must be at least 6 characters long.");
      return;
    }

    if (!payload.isAbove18) {
      setStatus("error");
      setMessage("You must confirm you are 18 or older.");
      return;
    }

    try {
      const response = await apiFetch("/auth/signup", {
        method: "POST",
        data: payload,
      });

      setStatus("success");
      setMessage(response?.message || "Signup successful. Redirecting…");

      if (response?.user) {
        saveAuthUser(response.user);
      }
      clearReferralCode();

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        city: "",
        password: "",
        isAbove18: false,
      });
      setShowPassword(false);

      // Redirect after success
      setTimeout(() => {
        navigate("/packages", { replace: true });
      }, 1200);
    } catch (error) {
      setStatus("error");
      setMessage(error?.data?.message || error.message || "Signup failed.");
    } finally {
      setStatus((current) => (current === "success" ? "success" : "idle"));
    }
  };

  return (
    <section className="relative w-full min-h-screen overflow-x-hidden overflow-y-auto bg-black pb-12 pt-6 font-[Poppins] sm:pb-20 sm:pt-8">
      {/* Top Shadow Image */}
      <img
        src="images/top.png"
        alt="Top Shadow"
        className="pointer-events-none absolute top-0 left-1/2 w-[140%] max-w-none -translate-x-1/2 opacity-45 sm:w-full sm:max-w-2xl sm:opacity-60"
      />

      {/* Left Corner */}
      <img
        src="images/leftcorner.png"
        alt="Left Corner"
        className="pointer-events-none absolute bottom-[-4rem] left-[-6rem] hidden w-48 opacity-50 sm:block sm:w-56 sm:opacity-70 md:bottom-0 md:left-0 md:w-64 md:opacity-80 lg:w-72"
      />

      {/* Right Corner */}
      <img
        src="images/right-corner.png"
        alt="Right Corner"
        className="pointer-events-none absolute bottom-[-4rem] right-[-6rem] hidden w-48 opacity-50 sm:block sm:w-56 sm:opacity-70 md:bottom-0 md:right-0 md:w-64 md:opacity-80 lg:w-72"
      />

      {/* Left Coin */}
      <img
        src="images/synergy.png"
        alt="Left Coin"
        className="pointer-events-none absolute left-[-5rem] top-1/2 hidden h-16 w-16 -translate-y-1/2 opacity-60 sm:left-12 sm:block sm:h-28 sm:w-28 sm:opacity-80 md:h-40 md:w-40"
      />

      {/* Right Coin */}
      <img
        src="images/synergy.png"
        alt="Right Coin"
        className="pointer-events-none absolute right-[-4rem] top-1/3 hidden h-12 w-12 -translate-y-1/2 opacity-60 sm:right-12 sm:block sm:h-20 sm:w-20 sm:opacity-80 md:h-28 md:w-28"
      />

      {/* Content */}
      <div className="relative z-20 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-5 sm:px-6">
          <img src="images/login-logo.png" alt="Logo" className="max-h-10 sm:max-h-12" />
          <button 
            onClick={() => navigate("/login")}
            className="text-white hover:text-purple-400 text-sm sm:text-base transition"
          >
            Login
          </button>
        </div>

        {/* Main Form */}
        <div className="flex items-start justify-center px-4 pb-16 pt-4 sm:items-center sm:px-6 sm:pb-16 sm:pt-6">
          <div className="w-full max-w-sm sm:max-w-md">
            <div className="rounded-[32px] px-5 py-6 sm:px-7 sm:py-8 sm:max-h-none sm:overflow-visible max-h-[calc(100vh-8rem)] overflow-y-auto">
              
              {/* Heading */}
              <div className="text-center mb-6">
               <h1
  className="text-2xl sm:text-3xl font-bold 
  bg-gradient-to-r from-[#ECEFF0] to-[#E5C0FF] 
  text-transparent bg-clip-text"
>
  Welcome to
</h1>

               <h2
  className="text-3xl sm:text-4xl font-bold mt-1 
  bg-gradient-to-r from-[#ECEFF0] to-[#E5C0FF] 
  text-transparent bg-clip-text"
>
  SynergyCoin
</h2>

                <p className="text-gray-400 text-sm mt-2">Sign up to continue</p>
              </div>

              {/* Google Button */}
              <div ref={googleButtonRef} className="w-full flex justify-center mb-5"></div>


              {/* Divider */}
              <div className="flex items-center gap-3 mb-5">
                <span className="flex-1 h-px bg-gray-700"></span>
                <span className="text-gray-400 text-sm">OR</span>
                <span className="flex-1 h-px bg-gray-700"></span>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} noValidate>
                {/* Full Name */}
                <label className="text-gray-300 text-sm mb-1 block">Full Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full mb-4 rounded-xl border border-white/12 bg-black/40 px-4 py-3 text-sm text-gray-100 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none"
                />

                {/* Email */}
                <label className="text-gray-300 text-sm mb-1 block">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Your email address"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full mb-4 rounded-xl border border-white/12 bg-black/40 px-4 py-3 text-sm text-gray-100 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none"
                />

                {/* Phone Number */}
                <label className="text-gray-300 text-sm mb-1 block">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="03XXXX XXX XXXX"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full mb-4 rounded-xl border border-white/12 bg-black/40 px-4 py-3 text-sm text-gray-100 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none"
                />

                {/* State / City */}
                <label className="text-gray-300 text-sm mb-1 block">State / City</label>
                <input
                  type="text"
                  name="city"
                  placeholder="Your city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full mb-4 rounded-xl border border-white/12 bg-black/40 px-4 py-3 text-sm text-gray-100 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none"
                />

                {/* Password */}
                <label className="text-gray-300 text-sm mb-1 block">Password</label>
                <div className="relative mb-4">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="****************"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/12 bg-black/40 px-4 py-3 pr-12 text-sm text-gray-100 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>

                {/* Checkbox */}
                <div className="flex items-center gap-2 mb-6">
                  <input
                    type="checkbox"
                    name="isAbove18"
                    checked={formData.isAbove18}
                    onChange={handleChange}
                    className="accent-purple-600 w-4 h-4"
                  />
                  <label className="text-gray-300 text-sm">Age should be 18+</label>
                </div>

                {/* Message Display */}
                {message && (
                  <div
                    className={`rounded-md px-4 py-2 text-sm mb-4 ${
                      status === "success"
                        ? "bg-emerald-500/10 text-emerald-300 border border-emerald-400/40"
                        : "bg-red-500/10 text-red-300 border border-red-400/40"
                    }`}
                  >
                    {message}
                  </div>
                )}

                {/* Sign Up Button */}
                <button 
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 disabled:cursor-not-allowed text-white py-3 rounded-lg mb-3 transition"
                >
                  {status === "loading" ? "Submitting..." : "Sign Up"}
                </button>
              </form>
            </div>

            <p className="text-center text-gray-400 text-xs mt-6 px-6">
              By creating an account, you agree to our terms and policies.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Signup;
